import { useQuery } from '@apollo/client';
import { AddCircle, Dvr } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { ListPagination } from '~/components/ListPagination';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { Count } from '~/components/count';
import { SearchBar } from '~/components/forms/search-bar';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import type { ChannelGuidesOrderBy } from '~/generated/graphql';
import { useOrderByParams } from '~/hooks/order-by';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import { NewChannelGuideDialog } from '../components/NewDialog';
import { ChannelGuideListDocument } from '../queries/list.generated';
import { BulkActions, Table } from './components';

export const ChannelGuideList = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const [searchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';

  const [newDialogOpen, setNewDialogOpen] = useState(false);

  const { orderBy } = useOrderByParams<ChannelGuidesOrderBy>();
  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const { data, loading, error } = useQuery(ChannelGuideListDocument, {
    variables: { networkId: currentNetwork.id, page, perPage, search, ...(orderBy && { orderBy }) },
    onCompleted: (data) => handleOutOfBounds(data.network?.channelGuides.pageInfo),
  });

  const totalCount = useTotalCount(data?.network?.channelGuides.pageInfo.nodesCount);
  const channelGuides = data?.network?.channelGuides.nodes ?? [];

  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);

  useEffect(() => setSelectedIds([]), [setSelectedIds]);

  const canDestroySelected = selectedIds.every((selectedId) => {
    const guide = channelGuides.find((guide) => guide.id === selectedId);
    return guide?.canDestroy.value;
  });

  if (error || (!loading && data?.network == null)) return <Navigate replace to="/not-found" />;

  return (
    <>
      <Helmet title={t('pages.channel_guides.title')} />
      <Toolbar
        actions={
          <Button
            color="primary"
            onClick={() => setNewDialogOpen(true)}
            startIcon={<AddCircle />}
            variant="contained"
          >
            {t('pages.channel_guides.new_button')}
          </Button>
        }
        titleIcon={<Dvr />}
        titleText={t('pages.channel_guides.title')}
      ></Toolbar>
      <ListPageContainer>
        <ListContainer>
          <ListHeader>
            <ListActions>
              <SearchBar
                placeholder={t('pages.channel_guides.search_placeholder')}
                search={search}
              />
              <Box sx={{ display: { md: 'flex', xs: 'none' }, alignItems: 'center' }}>
                {selectedIds.length > 0 ? (
                  <BulkActions canDestroySelected={canDestroySelected} selectedIds={selectedIds} />
                ) : null}
                <Count
                  selectedCount={selectedIds.length}
                  totalCount={totalCount}
                  thing="common.pluralize.channel_guide_plural"
                />
              </Box>
            </ListActions>
          </ListHeader>
          <ListContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <Table
                  channelGuides={channelGuides}
                  loading={loading}
                  onCheck={setSelectedIds}
                  selectedIds={selectedIds}
                />
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ListPagination totalCount={totalCount} />
              </Box>
            </Box>
          </ListContent>
        </ListContainer>
      </ListPageContainer>
      <NewChannelGuideDialog close={() => setNewDialogOpen(false)} open={newDialogOpen} />
    </>
  );
};
